import { useState, useEffect, useRef } from "react";
import { X, Printer, Search, User, Clock, MapPin, Car, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";

/**
 * Modal "Perfil do Visitante — Insights de comportamento".
 *
 * Fluxo:
 *  1. Operador digita CPF (parcial ou completo) ou nome do visitante.
 *  2. Sistema busca CPFs distintos que batem com o termo.
 *  3. Operador seleciona um CPF → carrega perfil completo (estatísticas + timeline).
 *  4. Botão "Imprimir/PDF" abre nova janela HTML pronta para impressão.
 */
export default function PerfilVisitanteModal({ open, onOpenChange, operatorNome, clienteNome }) {
  const [termo, setTermo] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [cpfSelecionado, setCpfSelecionado] = useState("");
  const [perfil, setPerfil] = useState(null);
  const [carregandoPerfil, setCarregandoPerfil] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTermo("");
      setResultados([]);
      setCpfSelecionado("");
      setPerfil(null);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if ((termo || "").trim().length < 2) {
      setResultados([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const r = await clienteApi.get(
          `/cliente/relatorios/perfil-visitante/buscar?q=${encodeURIComponent(termo.trim())}&limit=20`
        );
        setResultados(Array.isArray(r.data) ? r.data : []);
      } catch (err) {
        setResultados([]);
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [termo, open]);

  const carregarPerfil = async (cpf) => {
    setCpfSelecionado(cpf);
    setCarregandoPerfil(true);
    setPerfil(null);
    try {
      const r = await clienteApi.get(`/cliente/relatorios/perfil-visitante/${cpf}`);
      setPerfil(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar perfil");
    } finally {
      setCarregandoPerfil(false);
    }
  };

  const gerarRelatorioHTML = () => {
    if (!perfil) return;
    const now = new Date();
    const dataHora = `${now.toLocaleDateString("pt-BR")} ${now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
    const html = renderHtml(perfil, {
      operatorNome: operatorNome || "—",
      clienteNome: clienteNome || "",
      dataHora,
    });
    const win = window.open("", "perfil_visitante", "width=1200,height=800");
    if (!win) {
      toast.error("Popup bloqueado. Permita popups deste site para imprimir.");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="perfil-visitante-modal"
    >
      <div className="bg-brand-dark border border-brand-ice/20 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-ice/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <User className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-brand-ice">Perfil do Visitante</h2>
              <p className="text-xs text-brand-ice/60">Insights de comportamento</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="perfil-visitante-close"
            className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Busca */}
          <div>
            <label className="text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/70">
              Buscar por CPF ou Nome
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ice/40" />
              <input
                type="text"
                autoFocus
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                placeholder="Digite CPF (ex: 016.489.617-11) ou nome (ex: JOSE EMERSON)"
                data-testid="perfil-visitante-termo"
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-brand-ice/20 rounded-lg text-brand-ice text-sm placeholder:text-brand-ice/40 focus:outline-none focus:border-brand-light"
              />
            </div>
          </div>

          {/* Resultados de busca */}
          {termo.length >= 2 && (
            <div className="space-y-2">
              {buscando && <p className="text-xs text-brand-ice/60">Buscando...</p>}
              {!buscando && resultados.length === 0 && (
                <p className="text-xs text-amber-400">Nenhum visitante encontrado</p>
              )}
              {resultados.map((r) => (
                <button
                  key={r.cpf}
                  type="button"
                  onClick={() => carregarPerfil(r.cpf)}
                  data-testid={`perfil-visitante-result-${r.cpf}`}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-left transition-colors ${
                    cpfSelecionado === r.cpf
                      ? "bg-blue-500/20 border border-blue-400"
                      : "bg-white/5 border border-brand-ice/10 hover:bg-white/10 hover:border-brand-ice/20"
                  }`}
                >
                  <div>
                    <p className="text-sm text-brand-ice font-medium">{r.nome}</p>
                    <p className="text-xs text-brand-ice/60">CPF {r.cpf}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-brand-ice font-bold">{r.total_visitas}</p>
                    <p className="text-xs text-brand-ice/60">visitas</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Perfil carregado */}
          {carregandoPerfil && (
            <p className="text-sm text-brand-ice/60 text-center py-8">Carregando perfil...</p>
          )}

          {perfil && !carregandoPerfil && (
            <div className="space-y-4">
              {/* Identidade */}
              <div className="p-4 bg-white/5 rounded-lg border border-brand-ice/10">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-lg font-bold text-brand-ice">{perfil.identidade.nome}</p>
                    <p className="text-sm text-brand-ice/60">CPF {perfil.identidade.cpf}</p>
                    {perfil.identidade.ultimo_celular && (
                      <p className="text-xs text-brand-ice/50 mt-1">
                        Último celular: {perfil.identidade.ultimo_ddi} {perfil.identidade.ultimo_celular}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-brand-light">{perfil.identidade.total_visitas}</p>
                    <p className="text-xs text-brand-ice/60">visitas totais</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-brand-ice/10 text-xs">
                  <div>
                    <p className="text-brand-ice/50">Primeira visita</p>
                    <p className="text-brand-ice">{perfil.identidade.primeira_visita[0] || "—"} {perfil.identidade.primeira_visita[1] || ""}</p>
                  </div>
                  <div>
                    <p className="text-brand-ice/50">Última visita</p>
                    <p className="text-brand-ice">{perfil.identidade.ultima_visita[0] || "—"} {perfil.identidade.ultima_visita[1] || ""}</p>
                  </div>
                </div>
              </div>

              {/* Estatísticas — 4 cards de destaque */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={<Clock className="w-4 h-4" />} label="Perm. média/visita" value={perfil.estatisticas.permanencia_media_por_visita} />
                <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Perm. média/dia" value={perfil.estatisticas.permanencia_media_por_dia} highlight />
                <StatCard icon={<Clock className="w-4 h-4" />} label="Horário médio entrada" value={perfil.estatisticas.horario_medio_entrada} />
                <StatCard icon={<Clock className="w-4 h-4" />} label="Horário médio saída" value={perfil.estatisticas.horario_medio_saida} />
              </div>

              {/* Colunas: EQLs, Motivos, Veículos, Visitados */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TopList icon={<MapPin className="w-4 h-4" />} title="EQLs mais visitadas" items={perfil.estatisticas.top_eqls} labelKey="eql" />
                <TopList icon={<Users className="w-4 h-4" />} title="Pessoas visitadas" items={perfil.estatisticas.top_visitados} labelKey="nome" />
                <TopList icon={<Car className="w-4 h-4" />} title="Veículos usados" items={perfil.estatisticas.veiculos.map((v) => ({ ...v, label: `${v.placa}${v.modelo ? " (" + v.modelo + ")" : ""}` }))} labelKey="label" />
                <TopList icon={<TrendingUp className="w-4 h-4" />} title="Motivos frequentes" items={perfil.estatisticas.top_motivos} labelKey="motivo" />
              </div>

              {/* Preview da timeline (últimas 5) */}
              <div>
                <p className="text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/70 mb-2">
                  Últimas visitas (preview — no HTML completo aparecem todas)
                </p>
                <div className="space-y-1.5">
                  {perfil.timeline.slice(0, 5).map((v, i) => (
                    <div key={v.id || i} className="flex items-center gap-3 p-2 bg-white/5 rounded text-xs">
                      <span className="text-brand-ice/60 whitespace-nowrap">{v.data} {v.hora}</span>
                      <span className="text-brand-ice font-medium">{v.eql || "—"}</span>
                      <span className="text-brand-ice/70 truncate flex-1">{v.pessoa_visitada || v.local || "—"}</span>
                      {v.permanencia !== "—" && (
                        <span className="text-emerald-300 whitespace-nowrap">{v.permanencia}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-brand-ice/10">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="perfil-visitante-fechar"
            className="px-4 py-2 rounded-lg font-heading font-medium text-sm text-brand-ice/70 bg-white/5 border border-brand-ice/20 hover:bg-white/10 hover:text-brand-ice transition-colors"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={gerarRelatorioHTML}
            disabled={!perfil || carregandoPerfil}
            data-testid="perfil-visitante-imprimir"
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-brand-ice/10 disabled:text-brand-ice/40 disabled:cursor-not-allowed text-white font-heading font-bold text-sm rounded-lg transition-colors"
          >
            <Printer className="w-4 h-4" />
            Imprimir/PDF
          </button>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value, highlight }) => (
  <div className={`p-3 rounded-lg border ${highlight ? "bg-emerald-500/10 border-emerald-400/30" : "bg-white/5 border-brand-ice/10"}`}>
    <div className="flex items-center gap-1.5 text-brand-ice/60">
      {icon}
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    <p className={`text-xl font-bold mt-1 ${highlight ? "text-emerald-300" : "text-brand-ice"}`}>{value || "—"}</p>
  </div>
);

const TopList = ({ icon, title, items, labelKey }) => (
  <div className="p-3 bg-white/5 rounded-lg border border-brand-ice/10">
    <div className="flex items-center gap-1.5 text-brand-ice/70 mb-2">
      {icon}
      <span className="text-xs font-heading font-bold uppercase tracking-wider">{title}</span>
    </div>
    {(!items || items.length === 0) && <p className="text-xs text-brand-ice/40">Sem dados</p>}
    <div className="space-y-1">
      {(items || []).slice(0, 5).map((it, i) => (
        <div key={i} className="flex items-center justify-between text-xs">
          <span className="text-brand-ice truncate flex-1">{it[labelKey] || "—"}</span>
          <span className="text-brand-ice/60 ml-2 tabular-nums">{it.total}×</span>
        </div>
      ))}
    </div>
  </div>
);

// =============================================================================
// Geração do HTML imprimível (janela nova)
// =============================================================================
function renderHtml(perfil, meta) {
  const { operatorNome, clienteNome, dataHora } = meta;
  const id = perfil.identidade;
  const st = perfil.estatisticas;
  const tl = perfil.timeline || [];

  const listRow = (label, items, keyName) => {
    if (!items || items.length === 0) return `<tr><td class="k">${label}</td><td class="v">—</td></tr>`;
    return `<tr><td class="k">${label}</td><td class="v">${items.slice(0, 8).map((it) => `<span class="chip">${escapeHtml(it[keyName] || "")} <b>${it.total}×</b></span>`).join(" ")}</td></tr>`;
  };

  const tipoRow = (items) => {
    if (!items || items.length === 0) return `<tr><td class="k">Distribuição por tipo</td><td class="v">—</td></tr>`;
    return `<tr><td class="k">Distribuição por tipo</td><td class="v">${items.map((it) => `<span class="chip">${escapeHtml(it.tipo)} <b>${it.total}×</b></span>`).join(" ")}</td></tr>`;
  };

  const veiculosRow = (items) => {
    if (!items || items.length === 0) return `<tr><td class="k">Veículos usados</td><td class="v">—</td></tr>`;
    return `<tr><td class="k">Veículos usados</td><td class="v">${items.map((v) => `<span class="chip">${escapeHtml(v.placa)}${v.modelo ? " • " + escapeHtml(v.modelo) : ""}${v.cor ? " • " + escapeHtml(v.cor) : ""} <b>${v.total}×</b></span>`).join(" ")}</td></tr>`;
  };

  const timelineRows = tl.map((v) => `
    <tr>
      <td>${v.data} <span class="hora">${v.hora}</span></td>
      <td>${escapeHtml(v.tipo || "")}</td>
      <td><b>${escapeHtml(v.eql || "—")}</b><br/><span class="sub">${escapeHtml(v.local || "")}</span></td>
      <td>${escapeHtml(v.pessoa_visitada || "—")}</td>
      <td>${escapeHtml(v.motivo || "—")}</td>
      <td>${v.placa ? `<b>${escapeHtml(v.placa)}</b>${v.modelo ? "<br/><span class='sub'>" + escapeHtml(v.modelo) + "</span>" : ""}` : "—"}</td>
      <td class="ct">${v.entrada}</td>
      <td class="ct">${v.saida}</td>
      <td class="ct perm">${v.permanencia}</td>
    </tr>`).join("");

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<title>Perfil do Visitante — ${escapeHtml(id.nome)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #0f172a; margin: 0; padding: 16px; font-size: 12px; line-height: 1.4; }
  .toolbar { position: sticky; top: 0; background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 8px 12px; display: flex; gap: 8px; justify-content: flex-end; margin: -16px -16px 12px; }
  .toolbar button { padding: 6px 14px; border-radius: 6px; border: 1px solid #cbd5e1; background: white; cursor: pointer; font-size: 12px; font-weight: 500; }
  .toolbar .primary { background: #2563eb; color: white; border-color: #2563eb; }
  .toolbar button:hover { opacity: 0.9; }
  @media print { .toolbar { display: none; } body { padding: 0; } }
  h1 { font-size: 18px; margin: 0 0 4px; color: #0f172a; }
  h2 { font-size: 13px; margin: 16px 0 8px; padding-bottom: 4px; border-bottom: 2px solid #2563eb; color: #1e40af; text-transform: uppercase; letter-spacing: 0.5px; }
  .meta { color: #475569; font-size: 11px; margin-bottom: 12px; }
  .meta span { margin-right: 12px; }
  .banner { background: linear-gradient(90deg, #1e40af 0%, #2563eb 100%); color: white; padding: 12px 16px; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
  .banner .n { font-size: 20px; font-weight: 700; }
  .banner .c { font-size: 11px; opacity: 0.85; }
  .banner .r { text-align: right; }
  .banner .r .big { font-size: 28px; font-weight: 800; line-height: 1; }
  .cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
  .card { background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px; }
  .card.hl { background: #d1fae5; border-color: #34d399; }
  .card .lbl { font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #475569; }
  .card .val { font-size: 18px; font-weight: 700; color: #0f172a; margin-top: 2px; }
  .card.hl .val { color: #065f46; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.info { margin-bottom: 12px; }
  table.info td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  table.info td.k { width: 180px; font-weight: 600; color: #475569; background: #f8fafc; }
  table.info td.v { color: #0f172a; }
  .chip { display: inline-block; background: #eff6ff; color: #1e40af; padding: 2px 8px; border-radius: 999px; margin: 1px 2px; font-size: 10px; }
  .chip b { color: #1e3a8a; margin-left: 3px; }
  table.timeline { border: 1px solid #cbd5e1; }
  table.timeline thead { background: #1e40af; color: white; }
  table.timeline th { padding: 6px 8px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
  table.timeline td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  table.timeline tr:nth-child(even) td { background: #f8fafc; }
  table.timeline .hora { color: #64748b; font-size: 10px; }
  table.timeline .sub { color: #64748b; font-size: 10px; }
  table.timeline .ct { text-align: center; font-family: ui-monospace, monospace; }
  table.timeline .perm { font-weight: 700; color: #059669; }
  .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #64748b; text-align: center; }
</style>
</head><body>
<div class="toolbar">
  <button onclick="window.close()">Fechar</button>
  <button class="primary" onclick="window.print()">🖨 Imprimir / Salvar PDF</button>
</div>

<h1>Relatório de Visitantes — Insights de comportamento</h1>
<div class="meta">
  <span><b>Emitido em:</b> ${escapeHtml(dataHora)}</span>
  <span><b>Operador:</b> ${escapeHtml(operatorNome)}</span>
  ${clienteNome ? `<span><b>Cliente:</b> ${escapeHtml(clienteNome)}</span>` : ""}
</div>

<div class="banner">
  <div>
    <div class="n">${escapeHtml(id.nome)}</div>
    <div class="c">CPF ${escapeHtml(id.cpf)}${id.ultimo_celular ? " • " + escapeHtml(id.ultimo_ddi) + " " + escapeHtml(id.ultimo_celular) : ""}</div>
  </div>
  <div class="r">
    <div class="big">${id.total_visitas}</div>
    <div class="c">visitas totais</div>
  </div>
</div>

<h2>Indicadores de comportamento</h2>
<div class="cards">
  <div class="card hl"><div class="lbl">Perm. média/dia</div><div class="val">${st.permanencia_media_por_dia}</div></div>
  <div class="card"><div class="lbl">Perm. média/visita</div><div class="val">${st.permanencia_media_por_visita}</div></div>
  <div class="card"><div class="lbl">Horário médio entrada</div><div class="val">${st.horario_medio_entrada}</div></div>
  <div class="card"><div class="lbl">Horário médio saída</div><div class="val">${st.horario_medio_saida}</div></div>
</div>

<table class="info">
  <tr><td class="k">Primeira visita</td><td class="v">${id.primeira_visita[0] || "—"} ${id.primeira_visita[1] || ""}</td></tr>
  <tr><td class="k">Última visita</td><td class="v">${id.ultima_visita[0] || "—"} ${id.ultima_visita[1] || ""}</td></tr>
  <tr><td class="k">Dias distintos frequentados</td><td class="v">${st.total_dias_frequentados}</td></tr>
  <tr><td class="k">Visitas com ciclo completo</td><td class="v">${st.total_visitas_com_ciclo_completo} de ${id.total_visitas}</td></tr>
  ${tipoRow(st.distribuicao_tipos)}
  ${listRow("EQLs mais visitadas", st.top_eqls, "eql")}
  ${listRow("Locais de acesso", st.top_locais, "local")}
  ${listRow("Pessoas visitadas", st.top_visitados, "nome")}
  ${listRow("Motivos frequentes", st.top_motivos, "motivo")}
  ${veiculosRow(st.veiculos)}
</table>

<h2>Timeline — todas as visitas (${tl.length})</h2>
<table class="timeline">
  <thead>
    <tr>
      <th>Data / Hora</th>
      <th>Tipo</th>
      <th>EQL / Local</th>
      <th>Visitou</th>
      <th>Motivo</th>
      <th>Veículo</th>
      <th>Entrada</th>
      <th>Saída</th>
      <th>Perm.</th>
    </tr>
  </thead>
  <tbody>${timelineRows}</tbody>
</table>

<div class="footer">Brave Access — Perfil do Visitante — ${escapeHtml(dataHora)}</div>
</body></html>`;
}

function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
